import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, apiFetch } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Image, Zap, Loader2, Download, Sparkles, Upload, X, Check, Crop, Trash2, Music, Search, BookmarkPlus
} from 'lucide-react';
import MainNav from '../components/MainNav';
import SongsSidebar from '../components/SongsSidebar';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const STYLES = [
  { id: 'modern', name: 'Modern Minimal', desc: 'Clean lines, bold typography' },
  { id: 'vintage', name: 'Vintage Vinyl', desc: 'Retro 70s style, warm colors' },
  { id: 'abstract', name: 'Abstract Art', desc: 'Vibrant colors, fluid shapes' },
  { id: 'neon', name: 'Neon Cyberpunk', desc: 'Glowing lights, dark background' },
  { id: 'watercolor', name: 'Watercolor', desc: 'Soft brush strokes, dreamy' },
  { id: 'minimal', name: 'Ultra Minimal', desc: 'Simple shapes, monochrome' },
  { id: '3d', name: '3D Rendered', desc: 'CGI graphics, glossy surfaces' }
];

export default function CreateCover() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cropImageRef = useRef(null);
  const leftContentRef = useRef(null);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('modern');
  const [songs, setSongs] = useState([]);
  const [songsLoading, setSongsLoading] = useState(true);
  const [selectedSong, setSelectedSong] = useState('');
  const [songSearch, setSongSearch] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);  // Can be base64 or URL
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);  // HP server URL
  const [coverId, setCoverId] = useState(null);
  const [referenceImage, setReferenceImage] = useState(null);
  const [referencePreview, setReferencePreview] = useState(null);
  const [recentCovers, setRecentCovers] = useState([]);
  const [processingCoverId, setProcessingCoverId] = useState(null);
  const [coverCost, setCoverCost] = useState(3); // Default, will be fetched
  const [sidebarHeight, setSidebarHeight] = useState(null);
  
  // Cropping state
  const [showCropModal, setShowCropModal] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [tempImage, setTempImage] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 100, height: 100, x: 0, y: 0 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [deletingCoverId, setDeletingCoverId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);  // For inline delete confirmation
  const [songsRefreshTrigger, setSongsRefreshTrigger] = useState(0);
  const [applyingCoverId, setApplyingCoverId] = useState(null);
  
  // Album title overlay state
  const [albumTitle, setAlbumTitle] = useState('');
  const [titlePlacement, setTitlePlacement] = useState('bottom'); // 'top', 'middle', 'bottom'

  // Clear form on mount/navigation to this page
  useEffect(() => {
    // Reset form state when component mounts
    setPrompt('');
    setStyle('modern');
    setSelectedSong('');
    setGeneratedImage(null);
    setGeneratedImageUrl(null);
    setCoverId(null);
    setReferenceImage(null);
    setReferencePreview(null);
    setProcessingCoverId(null);
    setTempImage(null);
    setShowCropModal(false);
    setAlbumTitle('');
    setTitlePlacement('bottom');
  }, []); // Empty dependency - only on mount

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setTempImage(event.target.result);
      setCrop({ unit: '%', width: 100, height: 100, x: 0, y: 0 });
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const getCroppedImage = useCallback(() => {
    if (!completedCrop || !cropImageRef.current) return null;
    
    const image = cropImageRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );
    
    return canvas.toDataURL('image/jpeg', 0.9);
  }, [completedCrop]);

  const handleAcceptCrop = () => {
    const croppedDataUrl = getCroppedImage();
    if (croppedDataUrl) {
      setReferencePreview(croppedDataUrl);
      const base64 = croppedDataUrl.split(',')[1];
      setReferenceImage(base64);
    } else if (tempImage) {
      // No crop applied, use original
      setReferencePreview(tempImage);
      const base64 = tempImage.split(',')[1];
      setReferenceImage(base64);
    }
    setShowCropModal(false);
    setTempImage(null);
  };

  const handleCancelCrop = () => {
    setShowCropModal(false);
    setTempImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeReferenceImage = () => {
    setReferenceImage(null);
    setReferencePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Fetch songs and covers when user is authenticated
  useEffect(() => {
    if (!user) {
      console.log('CreateCover: No user, skipping fetch');
      return;
    }
    
    const fetchData = async () => {
      setSongsLoading(true);
      console.log('CreateCover: Fetching songs and covers...');
      try {
        const [songsResult, coversResult, costsResult] = await Promise.all([
          apiFetch('/songs').catch(e => { console.error('Songs fetch error:', e); return { data: [] }; }),
          apiFetch('/covers').catch(e => { console.error('Covers fetch error:', e); return { data: [] }; }),
          apiFetch('/credits/costs').catch(e => { console.error('Costs fetch error:', e); return { data: {} }; })
        ]);
        
        console.log('CreateCover: Fetched', { songs: songsResult.data?.length, covers: coversResult.data?.length });
        setSongs((songsResult.data || []).filter(s => s.status === 'completed'));
        setRecentCovers(coversResult.data || []);
        if (costsResult.data?.album_cover !== undefined) {
          setCoverCost(costsResult.data.album_cover);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setSongsLoading(false);
      }
    };
    fetchData();
  }, [user]);

  // Measure left content height for dynamic sidebar sizing
  useEffect(() => {
    const measureHeight = () => {
      if (leftContentRef.current) {
        setSidebarHeight(leftContentRef.current.offsetHeight);
      }
    };
    
    measureHeight();
    window.addEventListener('resize', measureHeight);
    return () => window.removeEventListener('resize', measureHeight);
  }, [generatedImage, generatedImageUrl, selectedSong, style]);

  const handleGenerate = async (freeRetry = false) => {
    // For "use_as_is" style, we need a reference image
    // For other styles with a reference image, prompt is optional (AI uses the image as reference)
    // For styles without a reference image, prompt is required
    if (style === 'use_as_is') {
      if (!referenceImage) {
        toast.error('Please upload an image first');
        return;
      }
    } else if (!referenceImage && !prompt.trim()) {
      // No image and no prompt - need at least one
      toast.error('Please describe your album cover or upload a reference image');
      return;
    }

    if (!freeRetry && coverCost > 0 && (user?.credits || 0) < coverCost) {
      toast.error(`Insufficient credits. You need ${coverCost} credits for album cover generation.`);
      return;
    }

    setIsGenerating(true);
    setGeneratedImage(null);
    setGeneratedImageUrl(null);  // Clear previous URL
    setProcessingCoverId(null);

    try {
      console.log('Calling /covers/generate API...');
      const { data } = await apiFetch('/covers/generate', {
        method: 'POST',
        body: JSON.stringify({
          prompt: style === 'use_as_is' ? '' : prompt,
          style,
          song_id: selectedSong || null,
          reference_image: referenceImage || null,
          free_retry: freeRetry,
          album_title: albumTitle || null,
          title_placement: albumTitle ? titlePlacement : null
        })
      });

      console.log('Cover generation response:', data);
      
      // If status is processing, start polling
      if (data.status === 'processing') {
        setCoverId(data.cover_id);
        setProcessingCoverId(data.cover_id);
        toast.info('Cover generation started... This may take a minute.');
        
        // Start polling for completion
        pollCoverStatus(data.cover_id, freeRetry);
      } else if (data.status === 'completed') {
        // Immediate completion (shouldn't happen with async but handle it)
        if (data.image_url) {
          setGeneratedImageUrl(data.image_url);
        } else if (data.image_base64) {
          setGeneratedImage(data.image_base64);
        }
        setCoverId(data.cover_id);
        toast.success('Album cover generated!');
        setIsGenerating(false);
        // Note: Don't call refreshUser() here as it can cause component unmount
      } else {
        console.error('Unexpected response status:', data.status);
        toast.error('Unexpected response from server');
        setIsGenerating(false);
      }
    } catch (error) {
      console.error('Cover generation error:', error);
      toast.error(error.message || 'Failed to generate cover');
      setIsGenerating(false);
    }
  };

  // Poll for cover completion
  const pollCoverStatus = async (coverId, wasFreeRetry) => {
    console.log('Starting poll for cover:', coverId);
    const maxAttempts = 60; // 2 minutes max
    let attempts = 0;
    
    const checkStatus = async () => {
      attempts++;
      console.log(`Poll attempt ${attempts} for cover ${coverId}`);
      try {
        const { data } = await apiFetch(`/covers/${coverId}`);
        console.log('Cover poll response:', data.status, 'image_url:', !!data.image_url);
        
        if (data.status === 'completed') {
          // Use image_url from HP server (preferred) or fall back to base64
          const imageUrl = data.image_url || null;
          const imageBase64 = data.image_base64 || null;
          
          console.log('Cover completed! URL:', imageUrl?.substring(0, 50), 'base64:', !!imageBase64);
          
          if (imageUrl) {
            setGeneratedImageUrl(imageUrl);
            setGeneratedImage(null);  // Clear base64 since we have URL
            console.log('Set generatedImageUrl to:', imageUrl);
          } else if (imageBase64) {
            setGeneratedImage(imageBase64);
            setGeneratedImageUrl(null);
            console.log('Set generatedImage (base64)');
          } else {
            console.error('Cover completed but no image data!');
          }
          
          setProcessingCoverId(null);
          setIsGenerating(false);
          
          // Check if fallback was used (reference image was ignored)
          if (data.used_fallback) {
            toast.warning('Your reference image could not be processed. The cover was generated from your description instead.', {
              duration: 8000
            });
          } else if (selectedSong) {
            toast.success('Album cover generated and applied to song!');
          } else {
            toast.success('Album cover generated!');
          }
          // Note: Don't call refreshUser() here as it can cause component unmount during auth re-check
          // Refresh recent covers
          const { data: covers } = await apiFetch('/covers');
          setRecentCovers(covers || []);
          return;
        } else if (data.status === 'failed' || data.error) {
          setProcessingCoverId(null);
          setIsGenerating(false);
          // Surface a friendlier message + a one-click Retry
          const rawErr = data.error || '';
          const isBudget = /budget|quota|exceed/i.test(rawErr);
          const message = isBudget
            ? 'Cover generation is temporarily unavailable. Please try again in a few minutes.'
            : "Couldn't generate cover. Please try again.";
          toast.error(message, {
            duration: 10000,
            action: {
              label: 'Retry',
              onClick: () => handleGenerate(true),
            },
          });
          return;
        }
        
        // Still processing, poll again
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 2000); // Poll every 2 seconds
        } else {
          setProcessingCoverId(null);
          setIsGenerating(false);
          toast.error('Cover generation timed out. Please try again.');
        }
      } catch (error) {
        console.error('Poll error:', error);
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 2000);
        } else {
          setProcessingCoverId(null);
          setIsGenerating(false);
          toast.error('Failed to check cover status');
        }
      }
    };
    
    // Start polling after a short delay
    setTimeout(checkStatus, 2000);
  };

  const handleDownload = async () => {
    if (!generatedImage && !generatedImageUrl) return;
    
    if (generatedImageUrl) {
      // Use backend proxy to download (avoids CORS issues)
      try {
        toast.info('Starting download...');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/covers/${coverId}/download`, { credentials: 'include' });
        if (!response.ok) {
          // Fallback to opening in new tab
          window.open(generatedImageUrl, '_blank');
          toast.success('Opening cover in new tab...');
          return;
        }
        const blob = await response.blob();
        const ext = { 'image/webp': 'webp', 'image/jpeg': 'jpg', 'image/png': 'png' }[blob.type] || 'jpg';
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `album-cover-${Date.now()}.${ext}`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        toast.success('Cover downloaded!');
      } catch (error) {
        console.error('Download error:', error);
        // Fallback to opening in new tab
        window.open(generatedImageUrl, '_blank');
        toast.success('Opening cover in new tab...');
      }
    } else {
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${generatedImage}`;
      link.download = `album-cover-${Date.now()}.png`;
      link.click();
      toast.success('Cover downloaded!');
    }
  };

  const handleCreateGift = () => {
    if (selectedSong && coverId) {
      navigate('/create-gift', { state: { songId: selectedSong, coverId } });
    } else {
      toast.info('Select a song to create a gift card');
    }
  };

  // Apply existing cover to selected song
  const handleApplyCoverToSong = async (cover) => {
    if (!selectedSong) {
      toast.error('Please select a song first');
      return;
    }
    
    // Only use HP server URL - no more base64
    if (!cover.image_url) {
      toast.error('Cover image not available');
      return;
    }
    
    setApplyingCoverId(cover.cover_id);
    try {
      await apiFetch(`/songs/${selectedSong}/cover`, {
        method: 'PUT',
        body: JSON.stringify({ image_url: cover.image_url })
      });
      // Update the songs state to reflect the new cover immediately
      setSongs(prevSongs => prevSongs.map(s => 
        s.song_id === selectedSong ? { ...s, image_url: cover.image_url } : s
      ));
      // Trigger sidebar refresh
      setSongsRefreshTrigger(prev => prev + 1);
      toast.success('Cover applied to song!');
    } catch (error) {
      console.error('Failed to apply cover:', error);
      toast.error('Failed to apply cover to song');
    } finally {
      setApplyingCoverId(null);
    }
  };

  // Save the selected song's current artwork into the gallery
  const [savingSongCover, setSavingSongCover] = useState(false);
  const handleSaveSongCoverToGallery = async () => {
    if (!selectedSong || savingSongCover) return;
    setSavingSongCover(true);
    try {
      const { data } = await apiFetch(`/covers/from-song/${selectedSong}`, { method: 'POST' });
      if (data?.already_saved) {
        toast.info('This cover is already in your gallery');
      } else {
        setRecentCovers(prev => [data, ...prev.filter(c => c.cover_id !== data.cover_id)]);
        toast.success('Current cover saved to your gallery');
      }
    } catch (error) {
      console.error('Failed to save song cover:', error);
      toast.error(error?.message || 'Could not save this cover');
    } finally {
      setSavingSongCover(false);
    }
  };

  // Delete a cover
  const handleDeleteCover = async (coverId, e) => {
    e.stopPropagation(); // Prevent triggering the apply action
    
    // Show inline confirmation
    setConfirmDeleteId(coverId);
  };
  
  const confirmDelete = async (coverId) => {
    setDeletingCoverId(coverId);
    setConfirmDeleteId(null);
    try {
      await apiFetch(`/covers/${coverId}`, { method: 'DELETE' });
      setRecentCovers(recentCovers.filter(c => c.cover_id !== coverId));
      toast.success('Cover deleted');
    } catch (error) {
      console.error('Failed to delete cover:', error);
      toast.error('Failed to delete cover');
    } finally {
      setDeletingCoverId(null);
    }
  };
  
  const cancelDelete = (e) => {
    e.stopPropagation();
    setConfirmDeleteId(null);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <MainNav />

      {/* Main Content with Sidebar */}
      <main className="pt-24 pb-12 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto flex gap-8 items-start">
          <div ref={leftContentRef} className="flex-1 min-w-0">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">🎨 Create your cover art</h1>
              <p className="text-zinc-400">Describe it—we'll design it for you</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Form */}
              <div className="glass-card p-6" data-testid="cover-form">
                <div className="flex items-center gap-3 mb-6 flex-wrap">
                  {selectedSong ? (
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-black/30 flex-shrink-0">
                      {songs.find(s => s.song_id === selectedSong)?.image_url ? (
                        <img 
                          src={songs.find(s => s.song_id === selectedSong)?.image_url}
                          alt="Selected song"
                          className="w-full h-full object-cover"
                          data-testid="selected-song-current-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Music className="h-5 w-5 text-white/30" />
                        </div>
                      )}
                    </div>
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-bold text-white">✨ Cover Creator</h2>
                    <p className="text-sm text-zinc-400">{coverCost} credit{coverCost !== 1 ? 's' : ''} per image</p>
                  </div>
                  {selectedSong && songs.find(s => s.song_id === selectedSong)?.image_url && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSaveSongCoverToGallery}
                      disabled={savingSongCover}
                      title="Keep this song's current artwork in your gallery so you can reuse it later"
                      className="border-white/20 text-zinc-200 hover:bg-white/10 flex-shrink-0 w-full sm:w-auto sm:ml-auto"
                      data-testid="save-song-cover-to-gallery-btn"
                    >
                      {savingSongCover
                        ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        : <BookmarkPlus className="h-4 w-4 mr-1.5" />}
                      Save current cover
                    </Button>
                  )}
                </div>

                <div className="space-y-6">
                  {/* Song Selection */}
                  <div>
                    <Label className="text-white mb-2 block">🎵 Attach to a song (optional)</Label>

                    {/* Search filter — helpful on mobile where the sidebar isn't visible */}
                    <div className="relative mb-2">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                      <Input
                        type="text"
                        value={songSearch}
                        onChange={(e) => setSongSearch(e.target.value)}
                        placeholder="Type to filter songs..."
                        className="input-dark pl-9 pr-9"
                        disabled={songsLoading || !songs.length}
                        data-testid="song-search"
                        autoComplete="off"
                      />
                      {songSearch && (
                        <button
                          type="button"
                          onClick={() => setSongSearch('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-white/10 text-zinc-400 hover:text-white"
                          aria-label="Clear search"
                          data-testid="song-search-clear"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>

                    {(() => {
                      // Build a sorted + filtered list. Always keep the currently
                      // selected song present so it doesn't disappear while typing.
                      const q = songSearch.trim().toLowerCase();
                      const sorted = [...songs].sort((a, b) =>
                        (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' })
                      );
                      const filtered = q
                        ? sorted.filter(
                            (s) =>
                              (s.title || '').toLowerCase().includes(q) ||
                              s.song_id === selectedSong
                          )
                        : sorted;
                      return (
                        <>
                          <select
                            value={selectedSong}
                            onChange={(e) => setSelectedSong(e.target.value)}
                            className="custom-select w-full"
                            disabled={songsLoading}
                            data-testid="song-select"
                          >
                            {songsLoading ? (
                              <option value="">Loading songs...</option>
                            ) : (
                              <>
                                <option value="">No song selected</option>
                                {filtered.map((song) => (
                                  <option key={song.song_id} value={song.song_id}>
                                    {song.title}
                                  </option>
                                ))}
                              </>
                            )}
                          </select>
                          {!songsLoading && songs.length > 0 && (
                            <p
                              className="text-xs text-zinc-500 mt-1"
                              data-testid="song-search-count"
                            >
                              {q
                                ? `${filtered.filter((s) => (s.title || '').toLowerCase().includes(q)).length} match${
                                    filtered.filter((s) => (s.title || '').toLowerCase().includes(q)).length === 1 ? '' : 'es'
                                  }`
                                : `${songs.length} song${songs.length === 1 ? '' : 's'} (A→Z)`}
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>

              {/* Album Title */}
              <div>
                <Label className="text-white mb-2 block">📝 Album Title (optional)</Label>
                <Input
                  value={albumTitle}
                  onChange={(e) => setAlbumTitle(e.target.value)}
                  placeholder="e.g. Summer Memories, Love Songs Vol. 1"
                  className="input-dark"
                  data-testid="album-title-input"
                  autoComplete="off"
                />
                <p className="text-xs text-zinc-500 mt-2">
                  Text to overlay on your cover image
                </p>
                {albumTitle && (
                  <div className="mt-3">
                    <Label className="text-white mb-2 block text-sm">Title Placement</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: 'top', label: 'Top' },
                        { id: 'middle', label: 'Middle' },
                        { id: 'bottom', label: 'Bottom' }
                      ].map((pos) => (
                        <button
                          key={pos.id}
                          type="button"
                          onClick={() => setTitlePlacement(pos.id)}
                          className={`p-2 rounded-lg text-sm transition-all ${
                            titlePlacement === pos.id
                              ? 'bg-primary/20 border border-primary/50 text-white'
                              : 'bg-black/20 border border-white/10 text-zinc-400 hover:border-white/20'
                          }`}
                        >
                          {pos.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Prompt */}
              <div>
                <Label className="text-white mb-2 block">🎨 What should your cover look like?</Label>
                <Input
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g. a neon city at night, soft watercolor flowers, vintage vinyl vibe"
                  className="input-dark"
                  data-testid="prompt-input"
                  autoComplete="off"
                />
                <p className="text-xs text-zinc-500 mt-2">
                  Describe the vibe, colors, or scene you want
                </p>
              </div>

              {/* Reference Image Upload */}
              <div>
                <Label className="text-white mb-2 block">🖼️ Use an inspiration image (optional)</Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                  data-testid="image-upload-input"
                />
                {!referencePreview ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-2 px-3 border border-dashed border-white/20 rounded-lg hover:border-primary/50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Upload className="h-4 w-4 text-zinc-400" />
                    <span className="text-sm text-zinc-400">Click to upload</span>
                  </button>
                ) : (
                  <div className="relative group">
                    <img 
                      src={referencePreview} 
                      alt="Reference" 
                      className="w-full h-32 object-cover rounded-lg border border-white/10 cursor-pointer"
                      onClick={() => setShowImagePreview(true)}
                    />
                    <div 
                      className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center cursor-pointer"
                      onClick={() => setShowImagePreview(true)}
                    >
                      <span className="text-white text-sm">Click to expand</span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeReferenceImage(); }}
                      className="absolute top-2 right-2 p-1 bg-black/70 rounded-full hover:bg-red-500/70 transition-colors z-10"
                    >
                      <X className="h-4 w-4 text-white" />
                    </button>
                  </div>
                )}
              </div>

              {/* Style Selection */}
              <div>
                <Label className="text-white mb-2 block">🎨 Choose a style</Label>
                <div className="grid grid-cols-2 gap-2">
                  {STYLES.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setStyle(s.id)}
                      className={`p-3 rounded-lg text-left transition-all ${
                        style === s.id 
                          ? 'bg-primary/20 border border-primary/50' 
                          : 'bg-black/20 border border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`style-${s.id}`}
                    >
                      <div className="font-medium text-white text-sm">{s.name}</div>
                      <div className="text-xs text-zinc-400">{s.desc}</div>
                    </button>
                  ))}
                  {/* Use image as is - only enabled when reference image is attached */}
                  <button
                    onClick={() => setStyle('use_as_is')}
                    disabled={!referenceImage}
                    className={`p-3 rounded-lg text-left transition-all ${
                      style === 'use_as_is'
                        ? 'bg-primary/20 border border-primary/50'
                        : !referenceImage
                          ? 'bg-black/10 border border-white/5 opacity-50 cursor-not-allowed'
                          : 'bg-black/20 border border-white/10 hover:border-white/20'
                    }`}
                    data-testid="style-use_as_is"
                  >
                    <div className={`font-medium text-sm ${!referenceImage ? 'text-zinc-500' : 'text-white'}`}>Use Image As Is</div>
                    <div className="text-xs text-zinc-400">{referenceImage ? 'No AI processing' : 'Attach image first'}</div>
                  </button>
                </div>
              </div>

              {/* Generate Button */}
              <Button
                onClick={() => handleGenerate(false)}
                disabled={isGenerating || (style === 'use_as_is' ? !referenceImage : (!prompt.trim() && !referenceImage))}
                className="btn-primary w-full"
                data-testid="generate-cover-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {style === 'use_as_is' ? 'Processing...' : 'Creating... (30-60 seconds)'}
                  </>
                ) : style === 'use_as_is' ? (
                  <>
                    🖼️ Use This Image {albumTitle ? '(with title)' : ''} ({coverCost} credit{coverCost !== 1 ? 's' : ''})
                  </>
                ) : (
                  <>
                    🎨 Create My Cover ({coverCost} credit{coverCost !== 1 ? 's' : ''})
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Preview */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-bold text-white mb-4">👀 Your album cover</h3>
            
            <div className="aspect-square rounded-xl overflow-hidden bg-black/30 flex items-center justify-center">
              {isGenerating ? (
                <div className="text-center">
                  <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto mb-4" />
                  <p className="text-zinc-400">Creating your masterpiece...</p>
                  <p className="text-zinc-500 text-sm mt-2">This may take up to 60 seconds</p>
                  {processingCoverId && (
                    <p className="text-zinc-600 text-xs mt-2">Cover ID: {processingCoverId}</p>
                  )}
                </div>
              ) : generatedImageUrl ? (
                <img 
                  src={generatedImageUrl}
                  alt="Generated Album Cover"
                  className="w-full h-full object-cover"
                  data-testid="generated-image"
                />
              ) : generatedImage ? (
                <img 
                  src={`data:image/png;base64,${generatedImage}`}
                  alt="Generated Album Cover"
                  className="w-full h-full object-cover"
                  data-testid="generated-image"
                />
              ) : (
                <div className="text-center text-zinc-500">
                  <Image className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>Your cover will appear here</p>
                </div>
              )}
            </div>

            {(generatedImage || generatedImageUrl) && (
              <div className="space-y-4 mt-4">
                <div className="flex gap-3">
                  <Button 
                    onClick={async () => {
                      // If a song is selected, apply the cover to it
                      if (selectedSong && generatedImageUrl) {
                        try {
                          await apiFetch(`/songs/${selectedSong}/cover`, {
                            method: 'PUT',
                            body: JSON.stringify({ image_url: generatedImageUrl })
                          });
                          // Update songs state immediately
                          setSongs(prevSongs => prevSongs.map(s => 
                            s.song_id === selectedSong ? { ...s, image_url: generatedImageUrl } : s
                          ));
                          // Trigger sidebar refresh
                          setSongsRefreshTrigger(prev => prev + 1);
                          toast.success('Album cover applied to song!');
                        } catch (error) {
                          toast.error('Failed to apply cover');
                        }
                      } else if (selectedSong && !generatedImageUrl) {
                        toast.error('Please wait for cover to finish uploading');
                      } else {
                        toast.success('Album cover accepted!');
                      }
                    }} 
                    className="btn-primary flex-1"
                    data-testid="accept-cover-btn"
                  >
                    ✅ Use This Cover
                  </Button>
                  <Button 
                    onClick={() => {
                      setGeneratedImage(null);
                      setGeneratedImageUrl(null);
                      setCoverId(null);
                      toast.info('Regenerating... (Free retry)');
                      handleGenerate(true);
                    }} 
                    disabled={isGenerating}
                    className="btn-secondary flex-1"
                    data-testid="regenerate-cover-btn"
                  >
                    {isGenerating ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <>🔄 Try Another</>
                    )}
                  </Button>
                </div>
                <div className="flex gap-3">
                  <Button onClick={handleDownload} variant="outline" className="flex-1 border-white/20 text-zinc-300 hover:bg-white/10">
                    ⬇️ Download Cover
                  </Button>
                  <Button onClick={handleCreateGift} variant="outline" className="flex-1 border-white/20 text-zinc-300 hover:bg-white/10">
                    🎁 Turn into a Gift Card
                  </Button>
                </div>
              </div>
            )}
          </div>
            </div>
          </div>

          {/* Songs Sidebar - Dynamic height to match left content */}
          <SongsSidebar refreshTrigger={songsRefreshTrigger} maxHeight={sidebarHeight} />
        </div>

        {/* Recent Covers Gallery - Horizontal Scroll */}
        {recentCovers.length > 0 && (
          <div className="w-full mt-12">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl font-bold text-white mb-4">🎧 Your recent covers</h2>
              <p className="text-zinc-400 text-sm mb-6">
                {selectedSong ? 'Tap one to use it on a song' : 'Select a song first to apply a cover'}
              </p>
            </div>
            <div className="max-w-7xl mx-auto">
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
              {recentCovers.map((cover) => (
                <div
                  key={cover.cover_id}
                  className="group relative flex-shrink-0 w-32 h-32 sm:w-40 sm:h-40 rounded-lg overflow-hidden bg-black/30 border border-white/10 hover:border-primary/50 transition-all"
                  data-testid={`recent-cover-${cover.cover_id}`}
                >
                  <button
                    onClick={() => handleApplyCoverToSong(cover)}
                    disabled={!selectedSong || applyingCoverId === cover.cover_id}
                    className="w-full h-full"
                  >
                    {cover.image_url ? (
                      <img 
                        src={cover.image_url}
                        alt={cover.prompt || 'Album cover'}
                        className="w-full h-full object-cover"
                      />
                    ) : cover.image_base64 ? (
                      <img 
                        src={`data:image/png;base64,${cover.image_base64}`}
                        alt={cover.prompt || 'Album cover'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="h-8 w-8 text-white/20" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      {applyingCoverId === cover.cover_id ? (
                        <Loader2 className="h-6 w-6 text-white animate-spin" />
                      ) : (
                        <span className="text-white text-xs px-2 py-1 bg-primary/80 rounded">
                          {selectedSong ? 'Apply to Song' : 'Select Song First'}
                        </span>
                      )}
                    </div>
                  </button>
                  
                  {/* Delete button */}
                  <button
                    onClick={(e) => handleDeleteCover(cover.cover_id, e)}
                    disabled={deletingCoverId === cover.cover_id}
                    className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    data-testid={`delete-cover-${cover.cover_id}`}
                  >
                    {deletingCoverId === cover.cover_id ? (
                      <Loader2 className="h-3 w-3 text-white animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3 text-white" />
                    )}
                  </button>
                  
                  {/* Inline Delete Confirmation Overlay */}
                  {confirmDeleteId === cover.cover_id && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center z-20 rounded-lg">
                      <div className="bg-black px-3 py-2 rounded-lg">
                        <p className="text-white text-xs mb-2 text-center font-medium">Delete this cover?</p>
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={(e) => { e.stopPropagation(); confirmDelete(cover.cover_id); }}
                            className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded transition-colors"
                          >
                            Delete
                          </button>
                          <button
                            onClick={cancelDelete}
                            className="px-3 py-1 bg-zinc-600 hover:bg-zinc-500 text-white text-xs rounded transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Crop Modal */}
      <Dialog open={showCropModal} onOpenChange={(open) => !open && handleCancelCrop()}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Crop className="h-5 w-5 text-primary" />
              Crop Reference Image
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-zinc-400 mb-4">
              Drag to select the area you want to use as reference. For best results, crop to a square.
            </p>
            
            {tempImage && (
              <div className="max-h-96 overflow-auto bg-black/30 rounded-lg p-2">
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={1}
                  circularCrop={false}
                >
                  <img 
                    ref={cropImageRef}
                    src={tempImage} 
                    alt="Crop preview"
                    className="max-w-full"
                  />
                </ReactCrop>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleCancelCrop}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button 
              onClick={handleAcceptCrop}
              className="btn-primary"
            >
              <Check className="h-4 w-4 mr-2" />
              Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog open={showImagePreview} onOpenChange={setShowImagePreview}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white">Inspiration Image</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            {referencePreview && (
              <img 
                src={referencePreview} 
                alt="Full preview" 
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowImagePreview(false)}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
